import {Add, Gesture, GridOn, OpenInNew, PictureAsPdf, Refresh} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment-timezone';
import React, {useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import FirmaService from 'src/modules/firma/firmaService';
import ModeloTareaService from 'src/modules/modeloTarea/modeloTareaService';
import Errors from 'src/modules/shared/error/errors';
import {
  buildSyntheticTaskFromModeloTarea,
  listModeloTareasByTitlePrefixes,
} from 'src/modules/tarea/buildBlankProcedureListFromModelo';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {
  emptyMedicionesParcialId23,
  isId23ModeloTareaTitle,
  shouldMarkPrimeroDelParcialOnCreate,
} from 'src/modules/tarea/medicionesParcialId23';
import {emptyMedicionesParcialId24, isId24ModeloTareaTitle} from 'src/modules/tarea/medicionesParcialId24';
import {DEFAULT_PARENT_FIELD_CELLS_PER_ROW} from 'src/modules/tarea/parentFieldLayout';
import {exportChildTasksTableBlankPdf} from 'src/modules/tarea/tareaParcialBlankPdf';
import {exportChildTasksTableAsExcel} from 'src/modules/tarea/tareaParcialExcel';
import {
  buildActiveParcialChildMap,
  getRecordId,
  sortChildTasksByEndDateAsc,
  sortParcialesByDateAsc,
} from 'src/modules/tarea/tareaParcialHelpers';
import {exportChildTasksTableAsPdf} from 'src/modules/tarea/tareaParcialPdf';
import tareaSelectors from 'src/modules/tarea/tareaSelectors';
import TareaService from 'src/modules/tarea/tareaService';
import {getTaskVisualLabel} from 'src/modules/tarea/taskVisualLabel';
import {
  filterChildrenByInspectionTab,
  hasUnverifiedChildrenOfInspectionType,
  pickModeloOptionByPrefix,
  resolveOtPremiumInspectionTabs,
} from 'src/modules/tarea/plastyvergParentFieldsConfig';
import {
  resolvePlastyvergArticuloTitulo,
  resolvePlastyvergArticuloVariant,
  titlesConflictId25AndId26,
} from 'src/modules/tarea/plastyvergArticuloToleranceBounds';
import {tasksMixId25AndId26} from 'src/view/tarea/list/tareaListPdfExportHelpers';
import {canUsePartialVerification, collectParcialExecutorIds, shouldBlockVerifierAsExecutor} from 'src/security/partialVerificationAccess';
import FirmaParcialSignPanel from 'src/view/firma/parcial/FirmaParcialSignPanel';
import Message from 'src/view/shared/message';
import Spinner from 'src/view/shared/Spinner';
import ClasificacionTagView from 'src/view/shared/view/ClasificacionTagView';
import Drawer from 'src/view/shared/view/Drawer';
import ParentAnswersPanel from 'src/view/tarea/children/ParentAnswersPanel';
import QuickCreateChildDialog from 'src/view/tarea/children/QuickCreateChildDialog';
import {useParentFieldsLayout} from 'src/view/tarea/children/useParentFieldsLayout';
import {useTareaChildTasksData} from 'src/view/tarea/children/useTareaChildTasksData';
import TareaFormPage from 'src/view/tarea/form/TareaFormPage';
import MedicionesParcialReadonlyView from 'src/view/tarea/parcial/MedicionesParcialReadonlyView';
import {
  AnswerTablesView,
  buildTaskSections,
  ParcialSectionAccordion,
  RequestVerificationDialog,
  TaskSection,
} from 'src/view/tarea/parcial/parcialTableShared';

const resolveEntityId = (value: any) => value?.id || value?._id || value?.value || value?.record?.id || value;

const getTaskTitle = (task: any) => task?.modeloTarea?.titulo || task?.tituloTarea || task?.label || i18n('entities.tarea.label');

const getParentTitle = (task: any) =>
  [getTaskVisualLabel(task), task?.tituloTarea || task?.modeloTarea?.titulo].filter(Boolean).join(' - ');

const formatDate = (value: any) => {
  if (!value) return '-';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
};

const BLANK_PDF_MODELO_PREFIXES = ['ID-23', 'ID-24', 'ID-25', 'ID-26'];

function TaskRow({task, onOpen}: {task: any; onOpen: (task: any) => void}) {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
      onClick={() => onOpen(task)}>
      <CardContent sx={{py: 1.25, '&:last-child': {pb: 1.25}}}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" noWrap>
                {getTaskVisualLabel(task) || '-'}
              </Typography>
              <ClasificacionTagView value={task?.estado} entity="tarea" fieldName="estado" />
            </Box>
            <Typography variant="body2" sx={{fontWeight: 600}} noWrap>
              {getTaskTitle(task)}
            </Typography>
            {task?.subtitulo && (
              <Typography variant="caption" color="textSecondary" noWrap display="block">
                {task.subtitulo}
              </Typography>
            )}
          </Box>
          <Tooltip title={i18n('entities.firma.parcial.openDetail')}>
            <OpenInNew fontSize="small" color="action" />
          </Tooltip>
        </Box>

        <Grid container spacing={1} mt={0.5}>
          <Grid item xs={4}>
            <Typography variant="caption" color="textSecondary">
              {i18n('entities.tarea.fields.progreso')}
            </Typography>
            <Typography variant="body2">{task?.progreso != null ? `${Number(task.progreso).toFixed(0)}%` : '-'}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="textSecondary">
              {i18n('entities.tarea.fields.fechaProgramacion')}
            </Typography>
            <Typography variant="body2">{formatDate(task?.fechaProgramacion || task?.fechaEjecucion)}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="textSecondary">
              {i18n('entities.tarea.fields.fechaLimite')}
            </Typography>
            <Typography variant="body2">{formatDate(task?.fechaLimite || task?.fechaLimiteApp || task?.finEstimado)}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function TareaChildTasksList(props) {
  const parentTask = useMemo(() => props.parentTask || {}, [props.parentTask]);
  const parentId = getRecordId(parentTask);
  const currentUser = useSelector(authSelectors.selectCurrentUser);
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const roles = useSelector(authSelectors.selectRoles);
  const hasPermissionToEdit = useSelector(tareaSelectors.selectPermissionToEdit);
  const hasPermissionToCreate = useSelector(tareaSelectors.selectPermissionToCreate);
  const partialVerificationEnabled = canUsePartialVerification(currentTenant?.id);
  const canExportBlankPdfTemplate = isPlastyvergTenant(currentTenant?.id) && (roles || []).includes('admin');

  const [selectedChild, setSelectedChild] = useState<any>(null);
  const [childDrawerVisible, setChildDrawerVisible] = useState(false);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [tableMode, setTableMode] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingBlankPdf, setExportingBlankPdf] = useState(false);
  const [exportingSectionKey, setExportingSectionKey] = useState<string | null>(null);
  const [signingParcial, setSigningParcial] = useState<any>(null);
  const [signDrawerVisible, setSignDrawerVisible] = useState(false);
  const [blankPdfMenuAnchorEl, setBlankPdfMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [blankModeloOptions, setBlankModeloOptions] = useState<Array<{id: string; titulo: string}>>([]);
  const [loadingBlankModelos, setLoadingBlankModelos] = useState(false);

  const data = useTareaChildTasksData({
    parentTask,
    tableMode,
    partialVerificationEnabled,
  });

  const parentForTabs = data.detailedParent || parentTask;
  const inspectionTabs = useMemo(
    () => resolveOtPremiumInspectionTabs(parentForTabs, data.children, currentTenant?.id),
    [parentForTabs, data.children, currentTenant?.id],
  );
  const showInspectionTabs = inspectionTabs.length > 1;
  const [activeInspectionTab, setActiveInspectionTab] = useState<'ID-23' | 'ID-24' | null>(null);

  const scopedTab = showInspectionTabs
    ? activeInspectionTab && inspectionTabs.includes(activeInspectionTab)
      ? activeInspectionTab
      : inspectionTabs[0]
    : null;
  const scopedListChildren = useMemo(
    () => filterChildrenByInspectionTab(data.children, scopedTab),
    [data.children, scopedTab],
  );
  const scopedDetailedChildren = useMemo(
    () => filterChildrenByInspectionTab(data.detailedChildren, scopedTab),
    [data.detailedChildren, scopedTab],
  );

  const fields = useParentFieldsLayout({
    parentTask: data.detailedParent || parentTask,
    fallbackParentTask: parentTask,
    childTasks: scopedDetailedChildren,
    listChildren: scopedListChildren,
    tenantId: currentTenant?.id,
    enabled: tableMode,
    // Plastyverg Ext/Sel puede resolver layout apenas hay padre; list ready también alcanza.
    dataReady: Boolean(data.detailedParent || parentTask?.listaProcedimiento?.length) && data.status !== 'listing',
    onConfigModeloAppFieldsUpdated: data.patchChildModeloAppFields,
    layoutMapKey: scopedTab,
  });

  const sortedChildren = useMemo(() => sortChildTasksByEndDateAsc(scopedListChildren), [scopedListChildren]);
  const sortedParciales = useMemo(() => sortParcialesByDateAsc(data.parciales), [data.parciales]);
  const tableTasks = useMemo(
    () => (scopedDetailedChildren.length ? sortChildTasksByEndDateAsc(scopedDetailedChildren) : sortedChildren),
    [scopedDetailedChildren, sortedChildren],
  );

  const parentDisplayProgress = useMemo(() => {
    if (data.children.length > 0) {
      const values = data.children.map((child) => Number(child?.progreso)).filter((value) => Number.isFinite(value));
      if (!values.length) return null;
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    }
    const parentProgress = Number(parentTask?.progreso);
    return Number.isFinite(parentProgress) ? parentProgress : null;
  }, [data.children, parentTask?.progreso]);

  const activeChildMap = useMemo(() => buildActiveParcialChildMap(sortedParciales), [sortedParciales]);

  const eligibleChildren = useMemo(
    () => sortedChildren.filter((child) => !activeChildMap.has(getRecordId(child))),
    [activeChildMap, sortedChildren],
  );

  const sections = useMemo(
    () => buildTaskSections(sortedChildren, sortedParciales, partialVerificationEnabled),
    [sortedChildren, sortedParciales, partialVerificationEnabled],
  );

  const tableSections = useMemo(
    () => buildTaskSections(tableTasks, sortedParciales, partialVerificationEnabled),
    [tableTasks, sortedParciales, partialVerificationEnabled],
  );

  const openChild = (child) => {
    setSelectedChild(child);
    setChildDrawerVisible(true);
  };

  const handleCreateChild = async (modeloTareaValue) => {
    const modelId = resolveEntityId(modeloTareaValue);
    if (!modelId || !currentUser?.id) return;

    setCreating(true);
    try {
      const modeloTarea = await ModeloTareaService.find(modelId);
      if (
        isPlastyvergTenant(currentTenant?.id) &&
        titlesConflictId25AndId26(resolvePlastyvergArticuloTitulo(parentTask), modeloTarea?.titulo)
      ) {
        Message.error(i18n('common.cannotNestId25UnderId26'));
        return;
      }
      const primeroDelParcial = shouldMarkPrimeroDelParcialOnCreate({
        tenantId: currentTenant?.id,
        modeloTareaTitulo: modeloTarea.titulo,
        hasUnverifiedChildren: hasUnverifiedChildrenOfInspectionType(eligibleChildren, modeloTarea.titulo),
      });
      const emptyMediciones = isId24ModeloTareaTitle(modeloTarea.titulo)
        ? emptyMedicionesParcialId24()
        : isId23ModeloTareaTitle(modeloTarea.titulo)
          ? emptyMedicionesParcialId23()
          : null;
      const response = await TareaService.create({
        web: true,
        createNewTask: false,
        task: {
          id: modeloTarea.id,
          label: modeloTarea.titulo,
          titulo: modeloTarea.titulo,
          record: modeloTarea,
        },
        taskSubtitle: '',
        priority: modeloTarea.prioridad || 'Baja',
        dirigidoA: 'Ejecutor',
        executors: [{id: currentUser.id, label: currentUser.fullName || currentUser.email}],
        planning: null,
        parentTask: {id: parentId},
        validators: [],
        acceptors: [],
        dateStart: moment().startOf('day').toISOString(),
        dateEnd: moment().endOf('day').toISOString(),
        assets: [],
        locations: [],
        clasificacion: modeloTarea.clasificacion || parentTask?.clasificacion || 'operativa',
        silent: true,
        ...(primeroDelParcial
          ? {
              primeroDelParcial: true,
              ...(emptyMediciones ? {medicionesParcial: emptyMediciones} : {}),
            }
          : {}),
      });

      const createdId = Array.isArray(response) ? response[0]?.id : response?.id;
      const createdTask = createdId ? await TareaService.find(createdId) : null;
      Message.success(i18n('entities.tarea.childTaskCreateSuccess'));
      setQuickCreateOpen(false);
      await data.reload({hydrate: tableMode});
      if (createdTask) openChild(createdTask);
    } catch (error) {
      Errors.handle(error);
    } finally {
      setCreating(false);
    }
  };

  const handleRequestVerification = async (validatorValue) => {
    const validatorId = resolveEntityId(validatorValue);
    const tareasVinculadas = eligibleChildren.map(getRecordId).filter(Boolean);
    if (!validatorId || !tareasVinculadas.length) return;
    if (isPlastyvergTenant(currentTenant?.id) && tasksMixId25AndId26(eligibleChildren)) {
      Message.error(i18n('common.cannotMixId25AndId26'));
      return;
    }
    if (
      shouldBlockVerifierAsExecutor(currentTenant?.id) &&
      collectParcialExecutorIds(eligibleChildren).includes(String(validatorId))
    ) {
      Message.error(i18n('entities.firma.parcial.verifierIsExecutor'));
      return;
    }

    setRequesting(true);
    try {
      await FirmaService.create({
        tipoFirma: 'parcial',
        tipoOperacion: 'Tarea',
        estado: 'Pendiente',
        objetivo: 'Validar',
        parentTask: parentId,
        tareasVinculadas,
        responsable: validatorId,
        descripcion: i18n('entities.firma.parcial.requestDescription', tareasVinculadas.length),
      });

      Message.success(i18n('entities.firma.parcial.requestSuccess'));
      setRequestDialogOpen(false);
      await data.reload({hydrate: false});
    } catch (error) {
      Message.error(i18n('entities.firma.parcial.requestFailed'));
      Errors.handle(error);
    } finally {
      setRequesting(false);
    }
  };

  const resolveHydratedSnapshot = async () => {
    if (data.detailedParent && data.detailedChildren.length > 0) {
      return {parent: data.detailedParent, childDetails: data.detailedChildren};
    }
    const snapshot = await data.hydrateSnapshot();
    if (snapshot) return snapshot;
    return {
      parent: data.detailedParent || parentTask,
      childDetails: data.detailedChildren.length ? data.detailedChildren : sortedChildren,
    };
  };

  const handleExportSectionPdf = async (section: TaskSection) => {
    if (!section.parcial) return;

    setExportingSectionKey(section.key);
    try {
      const {parent, childDetails} = await resolveHydratedSnapshot();
      const sectionTaskIds = new Set(section.tasks.map(getRecordId));
      const hydratedSectionTasks = sortChildTasksByEndDateAsc(
        filterChildrenByInspectionTab(
          childDetails.filter((task) => sectionTaskIds.has(getRecordId(task))),
          scopedTab,
        ),
      );

      if (!hydratedSectionTasks.length) return;

      await exportChildTasksTableAsPdf(
        {
          parentTask: parent,
          tasks: hydratedSectionTasks,
          parentQuestionKeys: fields.layout,
          title: section.title,
          parcial: section.parcial,
          parciales: [],
        },
        {pageOrientation: 'landscape'},
      );
    } catch (error) {
      Message.error(i18n('entities.firma.parcial.partialPdfExportError'));
      Errors.handle(error);
    } finally {
      setExportingSectionKey(null);
    }
  };

  const handleExportTablePdf = async () => {
    setExportingPdf(true);
    try {
      const {parent, childDetails} = await resolveHydratedSnapshot();
      const scopedDetails = filterChildrenByInspectionTab(childDetails, scopedTab);
      const scopedParciales = scopedTab
        ? sortedParciales.filter((parcial) =>
            (parcial?.tareasVinculadas || []).some((linked) =>
              scopedDetails.some((child) => String(getRecordId(child)) === String(getRecordId(linked))),
            ),
          )
        : sortedParciales;

      await exportChildTasksTableAsPdf(
        {
          parentTask: parent,
          tasks: sortChildTasksByEndDateAsc(scopedDetails),
          parentQuestionKeys: fields.layout,
          title: i18n('entities.tarea.answerTablePdfTitle'),
          parciales: scopedParciales,
        },
        {pageOrientation: 'landscape'},
      );
    } catch (error) {
      Message.error(i18n('entities.tarea.answerTablePdfError'));
      Errors.handle(error);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportTableExcel = async () => {
    setExportingExcel(true);
    try {
      const {parent, childDetails} = await resolveHydratedSnapshot();
      const hydratedTasks = sortChildTasksByEndDateAsc(filterChildrenByInspectionTab(childDetails, scopedTab));
      const hydratedSections = buildTaskSections(hydratedTasks, sortedParciales, partialVerificationEnabled);

      exportChildTasksTableAsExcel({
        parentTask: parent,
        tasks: hydratedTasks,
        parentQuestions: fields.questions,
        parentQuestionKeys: fields.layout,
        sections: hydratedSections,
        tenantId: currentTenant?.id,
        title: parent.tituloTarea || i18n('entities.tarea.answerTablePdfTitle'),
      });
    } catch (error) {
      Message.error(i18n('entities.tarea.answerTableExcelError'));
      Errors.handle(error);
    } finally {
      setExportingExcel(false);
    }
  };

  const handleOpenBlankPdfMenu = async (event: React.MouseEvent<HTMLElement>) => {
    if (!canExportBlankPdfTemplate) return;
    setBlankPdfMenuAnchorEl(event.currentTarget);
    setLoadingBlankModelos(true);
    try {
      const options = await listModeloTareasByTitlePrefixes(BLANK_PDF_MODELO_PREFIXES);
      setBlankModeloOptions(options);
    } catch (error) {
      Errors.handle(error);
      setBlankModeloOptions([]);
    } finally {
      setLoadingBlankModelos(false);
    }
  };

  const handleCloseBlankPdfMenu = () => {
    setBlankPdfMenuAnchorEl(null);
  };

  const handleExportBlankTablePdf = async (modeloId: string) => {
    if (!canExportBlankPdfTemplate || !modeloId) return;
    handleCloseBlankPdfMenu();
    setExportingBlankPdf(true);
    try {
      const modeloTarea = await ModeloTareaService.find(modeloId);
      const syntheticTask = buildSyntheticTaskFromModeloTarea(modeloTarea);
      if (!syntheticTask.listaProcedimiento?.length) {
        Message.error(i18n('entities.tarea.answerTablePdfError'));
        return;
      }

      let parent = data.detailedParent || parentTask;
      if (parentId && (!parent?.listaProcedimiento || parent?.listaProcedimiento?.length === 0)) {
        try {
          parent = await TareaService.find(parentId);
        } catch {
          // keep existing parent fallback
        }
      }

      const isArticuloTemplate = Boolean(resolvePlastyvergArticuloVariant(modeloTarea?.titulo));
      await exportChildTasksTableBlankPdf(
        {
          parentTask: isArticuloTemplate ? null : parent,
          tasks: [syntheticTask],
          parentQuestionKeys: isArticuloTemplate
            ? []
            : fields.layout || {cellsPerRow: DEFAULT_PARENT_FIELD_CELLS_PER_ROW, fields: []},
          title: i18n('entities.tarea.answerTablePdfTitle'),
          parciales: [],
        },
        {pageOrientation: 'landscape'},
      );
    } catch (error) {
      Message.error(i18n('entities.tarea.answerTablePdfError'));
      Errors.handle(error);
    } finally {
      setExportingBlankPdf(false);
    }
  };

  const handleChildRecordSaved = useCallback(
    ({updating}: {updating?: boolean} = {}) => {
      void data.reload({hydrate: tableMode});
      if (!updating) {
        setChildDrawerVisible(false);
        setSelectedChild(null);
      }
    },
    [data.reload, tableMode],
  );

  const handleNewChildClick = async () => {
    if (!scopedTab) {
      setQuickCreateOpen(true);
      return;
    }
    try {
      const options = await listModeloTareasByTitlePrefixes(['ID-23', 'ID-24']);
      const match = pickModeloOptionByPrefix(options, scopedTab);
      if (!match) {
        Message.error(i18n('entities.tarea.childTasksSelectModel'));
        return;
      }
      await handleCreateChild(match);
    } catch (error) {
      Errors.handle(error);
    }
  };

  const canRequestVerification = partialVerificationEnabled && eligibleChildren.length > 0;
  const canExportTablePdf = sortedChildren.length > 0;
  const showParentFields = !fields.ready || fields.questions.length > 0;

  const canSignParcial = useCallback(
    (parcial: any) => {
      const responsableId = getRecordId(parcial?.responsable);
      const executorBlocked =
        shouldBlockVerifierAsExecutor(currentTenant?.id) &&
        collectParcialExecutorIds(parcial).includes(String(currentUser?.id));
      return (
        parcial?.estado === 'Pendiente' &&
        parcial?.firmada !== true &&
        responsableId &&
        String(responsableId) === String(currentUser?.id) &&
        !executorBlocked
      );
    },
    [currentTenant?.id, currentUser?.id],
  );

  const openSignDrawer = (parcial: any) => {
    setSigningParcial(parcial);
    setSignDrawerVisible(true);
  };

  const handleParcialSigned = async () => {
    setSignDrawerVisible(false);
    setSigningParcial(null);
    const result = await data.reload({hydrate: tableMode});
    const remaining = (result?.parciales || []).filter(canSignParcial).length;
    props.onParcialSigned?.({closeOverlay: result ? remaining === 0 : false});
  };

  return (
    <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', pr: 1}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={1}>
        <Box>
          <Typography variant="h5">{i18n('entities.tarea.childTasksListTitle')}</Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n('entities.tarea.childTasksOrigin')}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            sx={{mr: 0}}
            control={<Switch checked={tableMode} onChange={(event) => setTableMode(event.target.checked)} size="small" />}
            label={i18n('entities.tarea.answerTableMode')}
          />
          <Tooltip title={i18n('common.refresh')}>
            <IconButton onClick={() => void data.reload({hydrate: tableMode})} disabled={data.busy}>
              {data.busy ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Card sx={{mb: 2}} variant="outlined">
        <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Box minWidth={0}>
              <Typography variant="subtitle1" noWrap>
                {getParentTitle(parentTask) || i18n('entities.tarea.label')}
              </Typography>
              {parentTask?.subtitulo && (
                <Typography variant="caption" color="textSecondary" display="block" noWrap>
                  {parentTask.subtitulo}
                </Typography>
              )}
            </Box>
            <ClasificacionTagView value={(data.detailedParent || parentTask)?.estado} entity="tarea" fieldName="estado" />
          </Box>
          <Grid container spacing={1} mt={0.5}>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                {i18n('entities.tarea.fields.progreso')}
              </Typography>
              <Typography variant="body2">
                {parentDisplayProgress != null ? `${parentDisplayProgress.toFixed(0)}%` : '-'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                {i18n('entities.tarea.fields.fechaProgramacion')}
              </Typography>
              <Typography variant="body2">{formatDate(parentTask?.fechaProgramacion || parentTask?.fechaEjecucion)}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        {hasPermissionToCreate && (
          <Button variant="contained" startIcon={<Add />} onClick={() => void handleNewChildClick()} disabled={creating}>
            {i18n('entities.tarea.newChildTask')}
          </Button>
        )}
        {partialVerificationEnabled && (
          <Button
            variant="outlined"
            startIcon={<Gesture />}
            disabled={!canRequestVerification}
            onClick={() => setRequestDialogOpen(true)}>
            {i18n('entities.firma.parcial.requestButton')}
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={exportingPdf ? <CircularProgress size={18} /> : <PictureAsPdf />}
          disabled={!canExportTablePdf || exportingPdf}
          onClick={handleExportTablePdf}>
          {i18n('entities.tarea.exportAnswerTablePdf')}
        </Button>
        <Button
          variant="outlined"
          startIcon={exportingExcel ? <CircularProgress size={18} /> : <GridOn />}
          disabled={!canExportTablePdf || exportingExcel}
          onClick={handleExportTableExcel}>
          {i18n('entities.tarea.exportAnswerTableExcel')}
        </Button>
        {canExportBlankPdfTemplate && (
          <>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={exportingBlankPdf || loadingBlankModelos ? <CircularProgress size={18} /> : <PictureAsPdf />}
              disabled={exportingBlankPdf || loadingBlankModelos}
              onClick={(event) => void handleOpenBlankPdfMenu(event)}>
              {i18n('entities.tarea.exportAnswerTableBlankPdf')}
            </Button>
            <Menu anchorEl={blankPdfMenuAnchorEl} open={Boolean(blankPdfMenuAnchorEl)} onClose={handleCloseBlankPdfMenu}>
              {loadingBlankModelos && (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{mr: 1}} />
                  {i18n('common.loading')}
                </MenuItem>
              )}
              {!loadingBlankModelos && blankModeloOptions.length === 0 && (
                <MenuItem disabled>{i18n('common.noDataToExport')}</MenuItem>
              )}
              {!loadingBlankModelos &&
                blankModeloOptions.map((option) => (
                  <MenuItem key={option.id} onClick={() => void handleExportBlankTablePdf(option.id)}>
                    {option.titulo}
                  </MenuItem>
                ))}
            </Menu>
          </>
        )}
      </Box>

      {showInspectionTabs && scopedTab && (
        <Tabs
          value={scopedTab}
          onChange={(_event, value: 'ID-23' | 'ID-24') => setActiveInspectionTab(value)}
          sx={{mb: 1, minHeight: 40}}
        >
          {inspectionTabs.map((tab) => (
            <Tab key={tab} value={tab} label={tab} sx={{minHeight: 40}} />
          ))}
        </Tabs>
      )}

      <Divider sx={{mb: 1}} />

      <Box sx={{overflowY: 'auto', flex: 1, pb: 2}}>
        {tableMode ? (
          <>
            {showParentFields && (
              <ParentAnswersPanel
                questions={fields.questions}
                layoutConfig={fields.layout}
                onToggle={fields.toggle}
                onReorder={fields.reorder}
                onColSpanChange={fields.setColSpan}
                onCellsPerRowChange={fields.setCellsPerRow}
                savingConfig={fields.saving}
                configReady={fields.ready}
              />
            )}
            {data.busy && <Spinner />}
            {!data.busy && tableTasks.length === 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {i18n('entities.tarea.childTasksEmpty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
            {!data.busy &&
              tableTasks.length > 0 &&
              tableSections.map((section) => {
                const sectionBody =
                  section.tasks.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n('entities.tarea.childTasksEmptySection')}
                    </Typography>
                  ) : (
                    <>
                      <MedicionesParcialReadonlyView tasks={section.tasks} />
                      <AnswerTablesView tasks={section.tasks} loading={false} onOpen={openChild} />
                    </>
                  );
                return partialVerificationEnabled ? (
                  <ParcialSectionAccordion
                    key={`${section.key}-${section.parcial?.estado || 'none'}`}
                    section={section}
                    canSignParcial={canSignParcial as any}
                    onSign={openSignDrawer}
                    onExportPdf={section.parcial ? () => void handleExportSectionPdf(section) : undefined}
                    exportingPdf={exportingSectionKey === section.key}
                    defaultExpanded={tableSections.length === 1}>
                    {sectionBody}
                  </ParcialSectionAccordion>
                ) : (
                  <Box key={section.key} mb={3}>
                    {sectionBody}
                  </Box>
                );
              })}
          </>
        ) : (
          <>
            {data.listing && <Spinner />}
            {!data.listing && sortedChildren.length === 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="textSecondary" textAlign="center">
                    {i18n('entities.tarea.childTasksEmpty')}
                  </Typography>
                </CardContent>
              </Card>
            )}
            {!data.listing &&
              sortedChildren.length > 0 &&
              sections.map((section) => {
                const sectionBody =
                  section.tasks.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {i18n('entities.tarea.childTasksEmptySection')}
                    </Typography>
                  ) : (
                    section.tasks.map((task) => <TaskRow key={getRecordId(task)} task={task} onOpen={openChild} />)
                  );
                return partialVerificationEnabled ? (
                  <ParcialSectionAccordion
                    key={`${section.key}-${section.parcial?.estado || 'none'}`}
                    section={section}
                    canSignParcial={canSignParcial as any}
                    onSign={openSignDrawer}
                    defaultExpanded={sections.length === 1}>
                    {sectionBody}
                  </ParcialSectionAccordion>
                ) : (
                  <Box key={section.key} mb={2}>
                    {sectionBody}
                  </Box>
                );
              })}
          </>
        )}
      </Box>

      <QuickCreateChildDialog
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onCreate={handleCreateChild}
        creating={creating}
      />
      <RequestVerificationDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        onRequest={handleRequestVerification}
        requesting={requesting}
        eligibleCount={eligibleChildren.length}
        excludeUserIds={shouldBlockVerifierAsExecutor(currentTenant?.id) ? collectParcialExecutorIds(eligibleChildren) : []}
      />
      <Drawer
        drawerId={'DrawerFirmaParcialSignPanel'}
        width={window.innerWidth > 768 ? 420 : window.innerWidth}
        visible={signDrawerVisible}
        setVisible={setSignDrawerVisible}
        preventBackdrop={true}
        onClose={() => {
          setSigningParcial(null);
        }}
        component={signingParcial ? <FirmaParcialSignPanel parcial={signingParcial} onSigned={handleParcialSigned} /> : null}
      />
      <Drawer
        drawerId={'DrawerTareaChildTaskFormPage'}
        width={window.innerWidth * 0.65}
        visible={childDrawerVisible}
        setVisible={setChildDrawerVisible}
        preventBackdrop={Boolean(selectedChild?.id)}
        promptBeforeExit={selectedChild?.id ? '¿Desea salir de la visualización?' : undefined}
        onClose={() => {
          setSelectedChild(null);
        }}
        component={
          selectedChild ? (
            <TareaFormPage
              sendFormSilent={props.sendFormSilent}
              tarea={selectedChild}
              disabled={!hasPermissionToEdit}
              drawer={true}
              keepDrawersOpen={true}
              onRecordSaved={handleChildRecordSaved}
            />
          ) : null
        }
      />
    </Box>
  );
}

export default TareaChildTasksList;
